import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import { Maximize2, X, ZoomIn, ZoomOut, RotateCcw, Shapes } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DiagramLegend } from './DiagramLegend'

interface Props {
  /** The diagram itself — the part fullscreen shows, zooms and pans. */
  children: ReactNode
  title?: string
  /** Interactive controls above the canvas (environment switch, step controls). */
  controls?: ReactNode
  /** Content under the canvas (a selected component's details, the current step). */
  panel?: ReactNode
}

export function DiagramViewer({ children, title, controls, panel }: Props) {
  const [fullscreen, setFullscreen] = useState(false)
  const [legendOpen, setLegendOpen] = useState(false)
  const [scale, setScale] = useState(1)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)
  const didDrag = useRef(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  const clampScale = useCallback((s: number) => Math.min(Math.max(s, 0.2), 6), [])

  const reset = useCallback(() => {
    setScale(1)
    setPos({ x: 0, y: 0 })
  }, [])

  useEffect(() => {
    if (!fullscreen) return
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const factor = e.deltaY > 0 ? -0.08 : 0.08
      setScale(s => clampScale(s + s * factor))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [fullscreen, clampScale])

  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false)
      if (e.key === '+' || e.key === '=') setScale(s => clampScale(s * 1.15))
      if (e.key === '-') setScale(s => clampScale(s * 0.85))
      if (e.key === '0') reset()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [fullscreen, clampScale, reset])

  useEffect(() => {
    if (!fullscreen) reset()
  }, [fullscreen, reset])

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    dragging.current = true
    didDrag.current = false
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
  }
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging.current) return
    didDrag.current = true
    setPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y })
  }
  const onMouseUp = () => { dragging.current = false }

  const ctrl = 'flex h-8 w-8 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white'

  return (
    <>
      <div className='relative my-6 overflow-hidden rounded-xl border bg-card shadow-sm'>
        <div className='flex items-center gap-1 border-b bg-muted/30 py-1 pl-4 pr-2'>
          <span className='flex-1 truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'>
            {title ?? 'Diagram'}
          </span>
          <button
            type='button'
            onClick={() => setLegendOpen(o => !o)}
            aria-expanded={legendOpen}
            title='What the shapes and colours mean'
            className={cn(
              'flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium transition-colors',
              legendOpen ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <Shapes className='h-3.5 w-3.5' />
            Legend
          </button>
          <button
            type='button'
            onClick={() => setFullscreen(true)}
            title='View fullscreen — zoom with scroll, pan with drag'
            aria-label='View diagram fullscreen'
            className='flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
          >
            <Maximize2 className='h-3.5 w-3.5' />
          </button>
        </div>
        {controls && <div className='border-b px-4 py-2.5'>{controls}</div>}
        {/* The diagram palettes are pastel with dark text, so the canvas stays light in dark mode. */}
        <div className='flex justify-center overflow-x-auto p-4 sm:p-6 dark:bg-slate-50 [&_svg]:mx-auto [&_svg]:max-w-full'>
          {children}
        </div>
        {panel && <div className='border-t'>{panel}</div>}
        {legendOpen && (
          <div className='border-t bg-muted/20 px-4 py-3'>
            <DiagramLegend compact />
          </div>
        )}
      </div>

      {fullscreen && (
        <div
          role='dialog'
          aria-modal='true'
          aria-label={title ? `${title}, fullscreen` : 'Diagram, fullscreen'}
          className='fixed inset-0 z-50 bg-black/92 backdrop-blur-md'
        >
          <div className='pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between border-b border-white/10 px-5 py-2.5'>
            <span className='text-sm font-medium text-white/70'>{title ?? 'Diagram'}</span>
            <div className='pointer-events-auto flex items-center gap-1'>
              <button onClick={() => setScale(s => clampScale(s * 1.2))} className={ctrl} title='Zoom in (+)'>
                <ZoomIn className='h-4 w-4' />
              </button>
              <span className='min-w-[3.5rem] text-center font-mono text-xs text-white/50'>
                {Math.round(scale * 100)}%
              </span>
              <button onClick={() => setScale(s => clampScale(s * 0.8))} className={ctrl} title='Zoom out (-)'>
                <ZoomOut className='h-4 w-4' />
              </button>
              <button onClick={reset} className={ctrl} title='Reset view (0)'>
                <RotateCcw className='h-3.5 w-3.5' />
              </button>
              <div className='mx-2 h-5 w-px bg-white/15' />
              <button onClick={() => setFullscreen(false)} className={ctrl} title='Close (Esc)'>
                <X className='h-4 w-4' />
              </button>
            </div>
          </div>

          <div className='pointer-events-none absolute inset-x-0 bottom-0 z-10 border-t border-white/10 px-5 py-1.5 text-center text-[10px] text-white/30'>
            Scroll to zoom · Drag to pan · + / - / 0 · Esc or click outside to close
          </div>

          <div
            ref={containerRef}
            className='absolute inset-0'
            style={{ cursor: dragging.current ? 'grabbing' : 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onClick={() => { if (!didDrag.current) setFullscreen(false) }}
          >
            <div
              className='flex h-full w-full items-center justify-center'
              style={{
                transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: dragging.current ? 'none' : 'transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                className='rounded-2xl bg-white p-5 shadow-2xl'
                style={{ height: 'calc(100vh - 40px)' }}
              >
                <div className='flex h-full items-center justify-center [&_svg]:!h-full [&_svg]:!w-auto [&_svg]:!max-w-[90vw]'>
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
